// services/techProgram.service.ts

import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type ProgramStatus = "completed" | "ongoing" | "upcoming";
export type TechProgramMode = "On-site" | "Online" | "Hybrid";

export interface TechProgramType {
  _id: string;
  name: string; // e.g. "Full Stack Web Development"
  vendor: string; // e.g. "CodeMaster Institute"
  technologies: string[]; // e.g. ["React", "Node.js", "MongoDB"]
  students: string[]; // Array of Student IDs
  departments: string[]; // Array of Dept IDs
  batches: string[]; // Array of Batch IDs
  duration: string; // e.g. "3 months"
  maxParticipants: number;
  mode: TechProgramMode;
  enrolled: number;
  completed: number;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  status: ProgramStatus;
  prerequisites: string;
  programObjectives: string;
  programDescription: string;
  createdAt: string;
  updatedAt: string;
}

export interface TechProgramCreateType {
  name: string;
  vendor: string;
  technologies: string[];
  students: string[];
  departments: string[];
  batches: string[];
  duration: string;
  maxParticipants: number;
  mode: TechProgramMode;
  enrolled: number;
  completed: number;
  startDate: string;
  endDate: string;
  status: ProgramStatus;
  prerequisites: string;
  programObjectives: string;
  programDescription: string;
}

export interface TechProgramMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type TechProgramParamsType = {
  search?: string;
  status?: ProgramStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_PROGRAMS: TechProgramType[] = [
  {
    _id: "1",
    name: "Full Stack Web Development",
    vendor: "1", // CodeMaster Institute
    technologies: ["React", "Node.js", "MongoDB"],
    students: ["1", "2"],
    departments: ["1", "2"],
    batches: ["1", "2"],
    duration: "3 months",
    maxParticipants: 75,
    mode: "On-site",
    enrolled: 75,
    completed: 62,
    startDate: "2024-10-01",
    endDate: "2025-01-01",
    status: "completed",
    prerequisites: "Basic HTML/CSS",
    programObjectives: "Learn MERN stack",
    programDescription: "Comprehensive web development",
    createdAt: "2024-09-01T00:00:00.000Z",
    updatedAt: "2024-09-01T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  programs: TechProgramType[],
  filters: TechProgramParamsType
): { data: TechProgramType[]; total: number } => {
  let result = [...programs];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.vendor.toLowerCase().includes(q) ||
        p.technologies.some((t) => t.toLowerCase().includes(q))
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((p) => p.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllTechPrograms = async (filters: TechProgramParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/tech-programs/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_PROGRAMS, filters);
  return {
    programs: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as TechProgramMetaType,
  };
};

export const fetchTechProgramById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/tech-programs/${_id}`);
  const program = DUMMY_PROGRAMS.find((p) => p._id === _id);
  if (!program) throw new Error("Program not found");
  return program;
};

export const createTechProgram = async (data: TechProgramCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/tech-programs/", data);
  const newProgram: TechProgramType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_PROGRAMS.push(newProgram);
  return newProgram;
};

export const updateTechProgram = async (
  _id: string,
  data: TechProgramCreateType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/tech-programs/${_id}`, data);
  const index = DUMMY_PROGRAMS.findIndex((p) => p._id === _id);
  if (index === -1) throw new Error("Program not found");
  DUMMY_PROGRAMS[index] = {
    ...DUMMY_PROGRAMS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_PROGRAMS[index];
};

export const deleteTechProgram = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/tech-programs/${_id}`);
  const index = DUMMY_PROGRAMS.findIndex((p) => p._id === _id);
  if (index === -1) throw new Error("Program not found");
  const [deleted] = DUMMY_PROGRAMS.splice(index, 1);
  return deleted;
};

export const fetchTechSkillVendorOptions = async (
  _params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  return {
    items: [
      { label: "DataScience Hub", value: "1" },
      { label: "CodeMaster Institute", value: "2" },
    ],
    hasMore: false,
    total: 2,
  };
};

export const techProgramService = {
  fetchAllTechPrograms,
  fetchTechProgramById,
  createTechProgram,
  updateTechProgram,
  deleteTechProgram,
  fetchTechSkillVendorOptions,
};
